import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import { LocalizationProvider, MobileDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import PaymentAttachmentUpload from "./PaymentAttachmentUpload";
import AttachmentLink from "./AttachmentLink";
import { uploadToVercelAPI } from "../config";
import { formatLocalDateTime } from "../utils/datetime";

const emptyForm = () => ({
  delivery_date: dayjs().format("YYYY-MM-DD"),
  received_by: "",
  delivered_by: "",
  receiver_phone: "",
  remarks: "",
  proof_url: "",
  proof_name: "",
  uploading: false,
});

// Confirming a delivery: every field is optional (the date defaults to today).
// Final — there is no un-confirm. onSubmit(values) -> Promise<boolean>.
export function DeliveryConfirmDialog({ open, docNumber, onClose, onSubmit }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    if (open) {
      setForm(emptyForm());
      setError("");
    }
  }, [open]);

  const submit = async () => {
    setSaving(true);
    setError("");
    try {
      const { uploading, ...values } = form;
      const message = await onSubmit(values);
      if (message) setError(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={Boolean(open)}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "14px" } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 1 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            bgcolor: "#E8F6EF",
            color: "#047857",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <LocalShippingOutlinedIcon />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 18, lineHeight: 1.2 }}>Confirm delivery</Typography>
          <Typography sx={{ fontSize: 13, color: "#64748B", fontWeight: 400 }}>{docNumber}</Typography>
        </Box>
        <IconButton size="small" onClick={onClose} disabled={saving} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13.5, color: "#475569", mb: 2 }}>
          Add anything you want recorded about the delivery — every field is optional. Confirming is final and cannot be
          undone.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <MobileDatePicker
                label="Delivered date"
                value={form.delivery_date}
                inputFormat="DD-MM-YYYY"
                maxDate={dayjs()}
                onChange={(d) => set({ delivery_date: d ? d.format("YYYY-MM-DD") : "" })}
                renderInput={(params) => <TextField {...params} size="small" fullWidth />}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              size="small"
              fullWidth
              label="Received by"
              placeholder="Name of the person who accepted"
              value={form.received_by}
              onChange={(e) => set({ received_by: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              size="small"
              fullWidth
              label="Delivered by"
              placeholder="Driver / salesperson"
              value={form.delivered_by}
              onChange={(e) => set({ delivered_by: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              size="small"
              fullWidth
              label="Receiver's phone"
              value={form.receiver_phone}
              onChange={(e) => set({ receiver_phone: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              size="small"
              fullWidth
              multiline
              rows={2}
              label="Remarks"
              placeholder="e.g. 2 boxes damaged, left with security"
              value={form.remarks}
              onChange={(e) => set({ remarks: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <PaymentAttachmentUpload
              height={48}
              label="Attach proof of delivery (signed copy / photo)"
              url={form.proof_url}
              name={form.proof_name}
              uploading={form.uploading}
              uploadApi={uploadToVercelAPI}
              onUploadStart={() => set({ uploading: true })}
              onUploadSuccess={({ url, name }) => set({ proof_url: url, proof_name: name, uploading: false })}
              onUploadError={(message) => {
                set({ uploading: false });
                setError(message);
              }}
              onRemove={() => set({ proof_url: "", proof_name: "" })}
            />
          </Grid>
        </Grid>
        {error && (
          <Typography sx={{ mt: 1.5, fontSize: 13, color: "#B42318" }} role="alert">
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} disabled={saving} color="inherit" variant="outlined" sx={{ textTransform: "none", borderRadius: "8px" }}>
          Cancel
        </Button>
        <LoadingButton
          onClick={submit}
          loading={saving}
          disabled={form.uploading}
          variant="contained"
          color="success"
          sx={{ textTransform: "none", fontWeight: 700, borderRadius: "8px", px: 2.5 }}
        >
          Confirm delivery
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

// Read-only: what was recorded when the delivery was confirmed.
export function DeliveryDetailsDialog({ open, row, onClose }) {
  const items = row
    ? [
        ["Delivered on", row.delivery_date || row.delivery_confirmed_at ? dayjs(row.delivery_date || row.delivery_confirmed_at).format("DD-MM-YYYY") : "-"],
        ["Received by", row.delivery_received_by || "-"],
        ["Delivered by", row.delivery_delivered_by || "-"],
        ["Receiver's phone", row.delivery_receiver_phone || "-"],
        ["Confirmed by", row.delivery_confirmed_by_name || "-"],
        ["Confirmed at", row.delivery_confirmed_at ? formatLocalDateTime(row.delivery_confirmed_at, "DD-MM-YYYY hh:mm A") : "-"],
      ]
    : [];
  return (
    <Dialog open={Boolean(open)} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "14px" } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", pr: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 18, lineHeight: 1.2 }}>Delivery details</Typography>
          <Typography sx={{ fontSize: 13, color: "#64748B", fontWeight: 400 }}>{row?.proforma_number}</Typography>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {items.map(([k, v]) => (
          <Box key={k} sx={{ display: "flex", justifyContent: "space-between", gap: 2, py: 0.6 }}>
            <Typography sx={{ fontSize: 13.5, color: "#64748B" }}>{k}</Typography>
            <Typography sx={{ fontSize: 13.5, fontWeight: 600, textAlign: "right" }}>{v}</Typography>
          </Box>
        ))}
        {row?.delivery_remarks && (
          <Box sx={{ mt: 1.5 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.4, color: "#64748B", textTransform: "uppercase" }}>
              Remarks
            </Typography>
            <Typography sx={{ fontSize: 13.5, mt: 0.25 }}>{row.delivery_remarks}</Typography>
          </Box>
        )}
        <Box sx={{ mt: 1.5 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.4, color: "#64748B", textTransform: "uppercase", mb: 0.5 }}>
            Proof of delivery
          </Typography>
          <AttachmentLink url={row?.delivery_proof_url} name={row?.delivery_proof_name} maxWidth={320} showEmpty />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="contained" sx={{ textTransform: "none", borderRadius: "8px" }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
